import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../../../redux/slices/appSlice";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Banner from "../../Utils/Snackbar";
import { useNavigate } from "react-router-dom";
import { logout } from "../../Utils/AuthService";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ChangePassword = ({ isDarkMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const username = useSelector((state) => state.user.userInfo.username);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [formValues, setFormValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = (name, value) => {
    let error = "";
    if (name === "currentPassword" && !value) {
      error = "Current password is required.";
    } else if (name === "newPassword") {
      if (!value) {
        error = "New password is required.";
      } else if (value.length < 8) {
        error = "Password must be at least 8 characters.";
      }
    } else if (name === "confirmNewPassword") {
      if (!value) {
        error = "Confirm new password is required.";
      } else if (value !== formValues.newPassword) {
        error = "Passwords do not match.";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    validate(name, value);
  };

  useEffect(() => {
    const allFieldsFilled = Object.values(formValues).every(
      (value) => value.trim() !== ""
    );
    const noErrors = Object.values(errors).every((error) => !error);
    setIsButtonDisabled(!(allFieldsFilled && noErrors));
  }, [formValues, errors]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (Object.values(errors).every((error) => !error)) {
      dispatch(
        changePassword({
          username,
          current_password: formValues.currentPassword,
          new_password: formValues.newPassword,
        })
      )
        .unwrap()
        .then(() => {
          setSnackbarMessage(
            "Your password has been changed successfully. You will be logged out shortly."
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);

          setTimeout(() => {
            logout();
            navigate("/login");
          }, 3000);
        })
        .catch((error) => {
          setSnackbarMessage(error);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 500,
        mx: "auto",
        mt: 5,
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: isDarkMode ? "#2E2E2E" : "#fff",
        color: isDarkMode ? "#f0f0f0" : "#1E243E",
      }}
    >
      <Typography
        variant="h5"
        mb={3}
        align="center"
        color={isDarkMode ? "#BDBDBD" : "#1E243E"}
      >
        Change Password
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            name="currentPassword"
            label="Current Password"
            variant="outlined"
            value={formValues.currentPassword}
            onChange={handleInputChange}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type={showPassword2 ? "text" : "password"}
            name="newPassword"
            label="New Password"
            variant="outlined"
            value={formValues.newPassword}
            onChange={handleInputChange}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword2((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword2 ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type={showConfirmPassword ? "text" : "password"}
            name="confirmNewPassword"
            label="Confirm New Password"
            variant="outlined"
            value={formValues.confirmNewPassword}
            onChange={handleInputChange}
            error={!!errors.confirmNewPassword}
            helperText={errors.confirmNewPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isButtonDisabled}
            sx={{
              backgroundColor: isButtonDisabled ? "#C8C8C8" : "#1E243E",
              color: "#fff",
              "&:hover": {
                backgroundColor: isButtonDisabled ? "#C8C8C8" : "#3A455C",
              },
            }}
          >
            Update Password
          </Button>
        </Grid>
      </Grid>
      <Banner
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default ChangePassword;
